import React, { useEffect, useMemo, useState } from 'react';
import { CompareView } from './RegistreFiscalCompare';
import axios from 'axios';
import RefreshButton from './ui/RefreshButton';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { DatePickerFr } from './ui/DatePickerFr';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  Receipt,
  Download,
  RefreshCw,
  Calendar,
  Filter as FilterIcon,
  XCircle,
  TrendingUp,
  FileText,
  Scale,
  Layers,
  GitCompareArrows,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  BookOpenCheck,
} from 'lucide-react';
import { getToken } from '../services/tokenService';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegistreFiscal() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');

  const headers = useMemo(() => ({ Authorization: `Bearer ${getToken()}` }), []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (periodStart) params.append('period_start', periodStart);
      if (periodEnd) params.append('period_end', periodEnd);
      const res = await axios.get(`${API}/registre-fiscal/?${params.toString()}`, { headers });
      setData(res.data);
    } catch {
      toast.error('Erreur lors du chargement du registre');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (periodStart) params.append('period_start', periodStart);
      if (periodEnd) params.append('period_end', periodEnd);
      const res = await axios.get(`${API}/registre-fiscal/export?${params.toString()}`, {
        headers, responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `registre_fiscal_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé');
    } catch {
      toast.error('Erreur lors de l\'export CSV');
    }
  };

  const handleDownloadPdf = async () => {
    try {
      const params = new URLSearchParams();
      if (periodStart) params.append('period_start', periodStart);
      if (periodEnd) params.append('period_end', periodEnd);
      const res = await axios.get(`${API}/registre-fiscal/pdf?${params.toString()}`, {
        headers, responseType: 'blob'
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `registre_fiscal_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success('PDF audit-ready téléchargé (signé numériquement)');
    } catch {
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailForm, setEmailForm] = useState({ to_email: '', to_name: '', custom_message: '' });
  const [sendingEmail, setSendingEmail] = useState(false);

  // Comparaison N vs N-1
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [loadingCompare, setLoadingCompare] = useState(false);
  const [downloadingBilan, setDownloadingBilan] = useState(false);

  const handleDownloadBilan = async () => {
    setDownloadingBilan(true);
    try {
      const response = await axios.get(`${API}/registre-fiscal/bilan-annuel/pdf`, {
        headers,
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `bilan_annuel_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Bilan annuel téléchargé · classé dans la GED');
    } catch {
      toast.error('Erreur lors du téléchargement du Bilan Annuel');
    } finally {
      setDownloadingBilan(false);
    }
  };

  const handleOpenCompare = async () => {
    setShowCompareModal(true);
    setLoadingCompare(true);
    setCompareData(null);
    try {
      const params = new URLSearchParams();
      if (periodStart) params.append('period_start', periodStart);
      if (periodEnd) params.append('period_end', periodEnd);
      const res = await axios.get(`${API}/registre-fiscal/compare?${params.toString()}`, { headers });
      setCompareData(res.data);
    } catch {
      toast.error('Erreur lors du chargement de la comparaison');
    } finally {
      setLoadingCompare(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailForm.to_email) {
      toast.error('Email du destinataire requis');
      return;
    }
    setSendingEmail(true);
    try {
      const res = await axios.post(`${API}/registre-fiscal/send-email`, {
        ...emailForm,
        period_start: periodStart || null,
        period_end: periodEnd || null,
      }, { headers });
      if (res.data.email_sent) {
        toast.success(`Registre envoyé à ${res.data.to} · Signature ${res.data.signature}`);
      } else {
        toast.info('Envoi simulé (service email non configuré)');
      }
      setShowEmailModal(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur envoi email');
    } finally {
      setSendingEmail(false);
    }
  };

  const resetPeriod = () => {
    setPeriodStart('');
    setPeriodEnd('');
    setTimeout(fetchData, 50);
  };

  const fmt = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const rows = data?.rows || [];
  const byLibelle = data?.by_libelle || [];
  const bySource = data?.by_source || [];

  const sourceMeta = (src) => {
    if (src === 'DOS DOMIUM') return { color: 'from-fuchsia-500 to-pink-600', icon: '🏗️' };
    if (src === 'Factures/Reçus ESS') return { color: 'from-blue-500 to-indigo-600', icon: '🤝' };
    if (src === 'LEGALCOOP') return { color: 'from-indigo-600 to-violet-700', icon: '⚖️' };
    return { color: 'from-teal-500 to-emerald-600', icon: '📋' };
  };

  return (
    <div className="space-y-6" data-testid="registre-fiscal">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-cyan-700 to-blue-800 shadow-xl">
        <div className="absolute -right-12 -top-12 w-64 h-64 rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="absolute -left-20 -bottom-20 w-72 h-72 rounded-full bg-cyan-300/20 blur-3xl pointer-events-none" />
        <div className="relative p-6 sm:p-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20 shadow-md">
                <Scale className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white">Registre Fiscal</h2>
                <p className="text-cyan-50/90 mt-1 text-sm">
                  Agrégation mensuelle des taxes personnalisées · Audit comptable · Déclaration octroi de mer
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <RefreshButton
                onRefresh={fetchData}
                loading={loading}
                successMessage="Registre fiscal actualisé"
                variant="light"
                testId="registre-refresh-btn"
              />
              <Button onClick={handleExport} variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/30 backdrop-blur-sm" data-testid="registre-export-csv-btn">
                <Download className="w-4 h-4 mr-2" />
                CSV
              </Button>
              <Button onClick={handleOpenCompare} variant="outline" className="bg-white/10 hover:bg-white/20 text-white border-white/30 backdrop-blur-sm" data-testid="registre-compare-btn">
                <GitCompareArrows className="w-4 h-4 mr-2" />
                Comparer N vs N-1
              </Button>
              <Button onClick={handleDownloadBilan} disabled={downloadingBilan} className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600 text-white shadow-md" data-testid="registre-bilan-annuel-btn">
                {downloadingBilan ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <BookOpenCheck className="w-4 h-4 mr-2" />}
                Bilan annuel
              </Button>
              <Button onClick={handleDownloadPdf} className="bg-white text-teal-700 hover:bg-cyan-50 shadow-md font-semibold" data-testid="registre-pdf-btn">
                <FileText className="w-4 h-4 mr-2" />
                PDF signé
              </Button>
              <Button onClick={() => setShowEmailModal(true)} className="bg-amber-400 text-amber-900 hover:bg-amber-300 shadow-md font-semibold" data-testid="registre-email-btn">
                <Receipt className="w-4 h-4 mr-2" />
                Envoyer au comptable
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="kpi-rise kpi-rise-0 relative overflow-hidden border-0 shadow-lg">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-teal-700" />
          <CardContent className="relative p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 rounded-lg bg-white/20 backdrop-blur"><TrendingUp className="w-5 h-5 text-white" /></div>
              <Badge className="bg-slate-900/70 text-white border border-white/30 text-[10px] font-bold tracking-wider px-2 py-0.5">HT</Badge>
            </div>
            <div className="text-3xl font-bold text-white tabular-nums">{fmt(data?.total_ht_global)} €</div>
            <div className="text-xs text-emerald-100 mt-1">Total HT taxé (documents distincts)</div>
          </CardContent>
        </Card>

        <Card className="kpi-rise kpi-rise-1 relative overflow-hidden border-0 shadow-lg">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500 to-orange-700" />
          <CardContent className="relative p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 rounded-lg bg-white/20 backdrop-blur"><Receipt className="w-5 h-5 text-white" /></div>
              <Badge className="bg-slate-900/70 text-white border border-white/30 text-[10px] font-bold tracking-wider px-2 py-0.5">COLLECTÉ</Badge>
            </div>
            <div className="text-3xl font-bold text-white tabular-nums">{fmt(data?.total_taxe_global)} €</div>
            <div className="text-xs text-amber-100 mt-1">Total taxes collectées (toutes lignes)</div>
          </CardContent>
        </Card>

        <Card className="kpi-rise kpi-rise-2 relative overflow-hidden border-0 shadow-lg">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-indigo-700" />
          <CardContent className="relative p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 rounded-lg bg-white/20 backdrop-blur"><FileText className="w-5 h-5 text-white" /></div>
              <Badge className="bg-slate-900/70 text-white border border-white/30 text-[10px] font-bold tracking-wider px-2 py-0.5">DOCS</Badge>
            </div>
            <div className="text-3xl font-bold text-white tabular-nums">{data?.nb_documents_distinct || 0}</div>
            <div className="text-xs text-blue-100 mt-1">Documents fiscalisés</div>
          </CardContent>
        </Card>

        <Card className="kpi-rise kpi-rise-3 relative overflow-hidden border-0 shadow-lg">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-fuchsia-700" />
          <CardContent className="relative p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 rounded-lg bg-white/20 backdrop-blur"><Layers className="w-5 h-5 text-white" /></div>
              <Badge className="bg-slate-900/70 text-white border border-white/30 text-[10px] font-bold tracking-wider px-2 py-0.5">TYPES</Badge>
            </div>
            <div className="text-3xl font-bold text-white tabular-nums">{byLibelle.length}</div>
            <div className="text-xs text-purple-100 mt-1">Taxes distinctes appliquées</div>
          </CardContent>
        </Card>
      </div>

      {/* Filtres période */}
      <Card className="border border-slate-200 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <FilterIcon className="w-4 h-4 text-teal-600" /> Période fiscale
            </div>
            <Button variant="ghost" size="sm" onClick={resetPeriod} className="text-slate-500 hover:text-slate-700">
              <XCircle className="w-4 h-4 mr-1" /> Toutes les périodes
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-slate-600 flex items-center gap-1"><Calendar className="w-3 h-3" /> Du</Label>
              <DatePickerFr className="mt-1 w-full" value={periodStart} onChange={setPeriodStart} id="registre-period-start" />
            </div>
            <div>
              <Label className="text-xs text-slate-600 flex items-center gap-1"><Calendar className="w-3 h-3" /> Au</Label>
              <DatePickerFr className="mt-1 w-full" value={periodEnd} onChange={setPeriodEnd} id="registre-period-end" />
            </div>
            <div className="flex items-end">
              <Button onClick={fetchData} disabled={loading} className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white shadow" data-testid="registre-filter-btn">
                <FilterIcon className="w-4 h-4 mr-2" /> Appliquer
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Récap par source (Plans / ESS / DOMIUM) */}
      {bySource.length > 0 && (
        <Card className="border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
            <div className="flex items-center gap-2 font-semibold text-slate-700 text-sm">
              <Scale className="w-4 h-4 text-amber-600" /> Récapitulatif par source
              <span className="text-xs font-normal text-slate-500 ml-2">— unifie Plans PADH-, Factures/Reçus ESS, DOS DOMIUM et LEGALCOOP</span>
            </div>
          </div>
          <CardContent className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {bySource.map((s) => {
                const m = sourceMeta(s.source);
                return (
                  <div key={s.source} className={`relative overflow-hidden rounded-lg p-4 text-white bg-gradient-to-br ${m.color} shadow-md`} data-testid={`registre-source-${s.source.replace(/\W+/g,'_')}`}>
                    <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/15" />
                    <div className="relative">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-semibold uppercase tracking-wider opacity-90">{s.source}</div>
                        <div className="text-2xl">{m.icon}</div>
                      </div>
                      <div className="text-2xl font-bold tabular-nums">{fmt(s.total_taxe)} €</div>
                      <div className="text-[11px] opacity-90 mt-1">Taxes collectées</div>
                      <div className="mt-2 pt-2 border-t border-white/25 flex justify-between text-[11px]">
                        <span>HT taxé : <strong>{fmt(s.total_ht_taxe)} €</strong></span>
                        <span>{s.nb_documents} doc{s.nb_documents > 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Récap par libellé */}
      {byLibelle.length > 0 && (
        <Card className="border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
            <div className="flex items-center gap-2 font-semibold text-slate-700 text-sm">
              <Layers className="w-4 h-4 text-purple-600" /> Récapitulatif par taxe
            </div>
          </div>
          <CardContent className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {byLibelle.map((b) => (
                <div key={b.libelle} className="rounded-lg border border-slate-200 p-4 hover:shadow-md transition-shadow" data-testid={`registre-libelle-${b.libelle.replace(/\W+/g,'_')}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-semibold text-slate-800">{b.libelle}</div>
                    <Badge className="bg-purple-600 text-white border-0 text-xs font-semibold">{b.nb_documents} doc{b.nb_documents > 1 ? 's' : ''}</Badge>
                  </div>
                  <div className="text-2xl font-bold text-teal-700 tabular-nums">{fmt(b.total_taxe)} €</div>
                  <div className="text-xs text-slate-500 mt-0.5">Taxe collectée cumulée</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tableau détaillé mois × taxe */}
      <Card className="border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2 font-semibold text-slate-700 text-sm">
            <Receipt className="w-4 h-4 text-teal-600" /> Détail mensuel par taxe
          </div>
          <Badge className="bg-teal-600 text-white border-0 text-xs font-semibold">{rows.length} ligne{rows.length > 1 ? 's' : ''}</Badge>
        </div>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/60">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-700 uppercase tracking-wider">Mois</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-700 uppercase tracking-wider">Source</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-700 uppercase tracking-wider">Libellé de la taxe</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-slate-700 uppercase tracking-wider">Type</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-slate-700 uppercase tracking-wider">Valeur</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-slate-700 uppercase tracking-wider">Total HT taxé</th>
                  <th className="text-right px-4 py-3 text-xs font-bold text-slate-700 uppercase tracking-wider">Taxe collectée</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-slate-700 uppercase tracking-wider">Documents</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr><td colSpan="8" className="py-12 text-center text-slate-400">
                    <RefreshCw className="w-5 h-5 inline animate-spin mr-2" /> Chargement…
                  </td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan="8" className="py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="p-4 rounded-full bg-slate-100"><Scale className="w-8 h-8 text-slate-400" /></div>
                      <div>
                        <div className="font-semibold text-slate-700">Aucune taxe enregistrée</div>
                        <div className="text-sm text-slate-500 mt-1">
                          Les taxes personnalisées définies sur les Plans/Tarifs apparaîtront ici dès la première facture émise.
                        </div>
                      </div>
                    </div>
                  </td></tr>
                )}
                {!loading && rows.map((r, i) => (
                  <tr key={i} className="hover:bg-teal-50/30 transition-colors">
                    <td className="px-4 py-3">
                      <Badge className="bg-slate-900 text-white font-mono text-xs font-semibold">{r.mois}</Badge>
                    </td>
                    <td className="px-4 py-3">
                      {r.source === 'DOS DOMIUM' ? (
                        <Badge className="bg-fuchsia-600 text-white border-0 text-[10px] font-semibold">🏗️ DOMIUM</Badge>
                      ) : r.source === 'Factures/Reçus ESS' ? (
                        <Badge className="bg-blue-600 text-white border-0 text-[10px] font-semibold">🤝 ESS</Badge>
                      ) : r.source === 'LEGALCOOP' ? (
                        <Badge className="bg-indigo-700 text-white border-0 text-[10px] font-semibold">⚖️ LEGALCOOP</Badge>
                      ) : (
                        <Badge className="bg-teal-600 text-white border-0 text-[10px] font-semibold">📋 Plans</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-800">{r.libelle}</td>
                    <td className="px-4 py-3 text-center">
                      {r.type === 'pourcentage' ? (
                        <Badge className="bg-blue-600 text-white border-0 text-xs font-semibold">Pourcentage</Badge>
                      ) : (
                        <Badge className="bg-purple-600 text-white border-0 text-xs font-semibold">Montant fixe</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {r.type === 'pourcentage' ? `${fmt(r.valeur)} %` : `${fmt(r.valeur)} €`}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-700">{fmt(r.total_ht_taxe)} €</td>
                    <td className="px-4 py-3 text-right tabular-nums font-bold text-teal-700">{fmt(r.total_taxe)} €</td>
                    <td className="px-4 py-3 text-center text-slate-600">{r.nb_documents}</td>
                  </tr>
                ))}
              </tbody>
              {!loading && rows.length > 0 && (
                <tfoot>
                  <tr className="bg-slate-100 border-t-2 border-slate-300 font-bold">
                    <td className="px-4 py-3 text-slate-800" colSpan="5">TOTAL</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-800">
                      {fmt(data?.total_ht_global)} €
                      <div className="text-[10px] font-normal text-slate-500">(documents distincts)</div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-teal-700">{fmt(data?.total_taxe_global)} €</td>
                    <td className="px-4 py-3 text-center text-slate-800">{data?.nb_documents_distinct || 0}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Modal envoi par email */}
      {showEmailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowEmailModal(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6" data-testid="registre-email-modal">
            <div className="flex items-center gap-3 mb-4 pb-3 border-b">
              <div className="p-2 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600">
                <Receipt className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800">Envoyer le Registre Fiscal</h3>
                <p className="text-xs text-slate-500">PDF audit-ready + signature numérique · pièce-jointe Brevo</p>
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-sm">Email du destinataire *</Label>
                <Input type="email" placeholder="comptable@cabinet.fr" value={emailForm.to_email} onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })} data-testid="registre-email-to" />
              </div>
              <div>
                <Label className="text-sm">Nom (optionnel)</Label>
                <Input placeholder="Cabinet Comptable XYZ" value={emailForm.to_name} onChange={(e) => setEmailForm({ ...emailForm, to_name: e.target.value })} data-testid="registre-email-name" />
              </div>
              <div>
                <Label className="text-sm">Message personnalisé (optionnel)</Label>
                <textarea
                  className="w-full h-20 p-2 border border-slate-300 rounded-md text-sm"
                  placeholder="Veuillez trouver ci-joint…"
                  value={emailForm.custom_message}
                  onChange={(e) => setEmailForm({ ...emailForm, custom_message: e.target.value })}
                  data-testid="registre-email-msg"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setShowEmailModal(false)}>Annuler</Button>
              <Button onClick={handleSendEmail} disabled={sendingEmail || !emailForm.to_email} className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white" data-testid="registre-email-send">
                {sendingEmail ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Receipt className="w-4 h-4 mr-2" />}
                Envoyer maintenant
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal comparaison N vs N-1 */}
      {showCompareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={() => setShowCompareModal(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full p-6 my-8" data-testid="registre-compare-modal">
            <div className="flex items-center justify-between mb-4 pb-3 border-b">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600">
                  <GitCompareArrows className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800">Comparaison N vs N-1</h3>
                  <p className="text-xs text-slate-500">Évolution des taxes collectées sur la même période · année courante vs précédente</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowCompareModal(false)} data-testid="registre-compare-close">
                <XCircle className="w-5 h-5" />
              </Button>
            </div>

            {loadingCompare ? (
              <div className="py-16 text-center text-slate-400">
                <RefreshCw className="w-6 h-6 inline animate-spin mr-2" /> Calcul de la comparaison…
              </div>
            ) : compareData ? (
              <CompareView data={compareData} fmt={fmt} />
            ) : (
              <div className="py-16 text-center text-slate-400">Aucune donnée</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Sous-composant : vue de comparaison N vs N-1
// ============================================================
